
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { recordAuditLog } from '../../hooks/useAuditLog';

interface AdminGuardProps {
  children: React.ReactNode;
}

export function AdminGuard({ children }: AdminGuardProps) {
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasLoggedAttempt = useRef(false);

  // セッション切れ・ログアウトをリアルタイムで検知
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        navigate('/', { replace: true });
      }
      if (!session && (event === 'SIGNED_OUT' || event === 'USER_DELETED')) {
        navigate('/', { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // /admin 配下にいる間、定期的にセッション有効性を確認（60秒ごと）
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return;

    const interval = setInterval(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        await recordAuditLog({
          event_type: 'session_expired',
          severity: 'warning',
          path: location.pathname,
          details: { reason: 'periodic_check_no_session' },
        });
        navigate('/', { replace: true });
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [location.pathname, navigate]);

  // 認証状態が確定した後、未認証 or 非管理者はリダイレクト＋ログ記録
  useEffect(() => {
    if (loading) return;

    if (!user) {
      // 未ログインでの直接アクセス
      if (!hasLoggedAttempt.current) {
        hasLoggedAttempt.current = true;
        recordAuditLog({
          event_type: 'unauthorized_access_attempt',
          severity: 'warning',
          user_id: null,
          user_email: null,
          path: location.pathname,
          details: {
            reason: 'not_authenticated',
            attempted_path: location.pathname,
          },
        });
      }
      navigate('/', { replace: true });
      return;
    }

    if (!isAdmin) {
      // ログイン済みだが管理者権限なし
      if (!hasLoggedAttempt.current) {
        hasLoggedAttempt.current = true;
        recordAuditLog({
          event_type: 'permission_denied',
          severity: 'critical',
          user_id: user.id,
          user_email: user.email ?? null,
          path: location.pathname,
          details: {
            reason: 'not_admin',
            attempted_path: location.pathname,
            user_id: user.id,
          },
        });
      }
      navigate('/', { replace: true });
    }
  }, [loading, user, isAdmin, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <i className="ri-loader-4-line text-4xl text-[#00ff41] animate-spin" />
          <p className="text-gray-500 text-sm tracking-wider">認証確認中...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return null;
  }

  return <>{children}</>;
}
